import { useState } from "react";
import { Mail, Send, Loader2, CheckCircle, User, MessageSquare } from "lucide-react";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim() || "Message via Profimy Margin",
        message: message.trim(),
      });
      setSent(true);
      toast.success("Message envoyé avec succès !");
    } catch {
      toast.error("Erreur lors de l'envoi. Réessayez ou écrivez à joseph@profimy-academie.com");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-8" data-testid="contact-page">
        <div className="flex flex-col items-center justify-center py-20 gap-6">
          <div className="bg-[#00E676] p-6 border-2 border-[#0A0A0A]">
            <CheckCircle className="text-[#0A0A0A]" size={48} />
          </div>
          <h2 className="text-3xl font-extrabold text-center">Message envoyé !</h2>
          <p className="text-muted-foreground text-center max-w-md">
            Merci pour votre message. Nous vous répondrons dans les plus brefs délais.
          </p>
          <button
            onClick={() => { setSent(false); setName(""); setEmail(""); setSubject(""); setMessage(""); }}
            className="neo-button-secondary"
            data-testid="send-another-btn"
          >
            Envoyer un autre message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="contact-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3">
          <Mail className="text-[#00E676]" size={40} />
          CONTACT
        </h1>
        <p className="text-muted-foreground mt-2 uppercase tracking-wider text-sm">
          Une question ? Un problème ? Écrivez-nous
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Form */}
        <div className="md:col-span-2">
          <div className="neo-card p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold uppercase tracking-wider block mb-2">
                    Nom *
                  </label>
                  <Input
                    type="text"
                    placeholder="Votre nom"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="neo-input w-full"
                    data-testid="contact-name"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-bold uppercase tracking-wider block mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="neo-input w-full"
                    data-testid="contact-email"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-bold uppercase tracking-wider block mb-2">
                  Sujet
                </label>
                <Input
                  type="text"
                  placeholder="Ex: Question sur l'abonnement"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="neo-input w-full"
                  data-testid="contact-subject"
                />
              </div>

              <div>
                <label className="text-sm font-bold uppercase tracking-wider block mb-2">
                  Message *
                </label>
                <textarea
                  placeholder="Votre message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="neo-input w-full resize-none"
                  data-testid="contact-message"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={sending || !name.trim() || !email.trim() || !message.trim()}
                className="neo-button-primary w-full sm:w-auto flex items-center justify-center gap-2"
                data-testid="contact-submit-btn"
              >
                {sending ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Send size={20} />
                )}
                Envoyer le message
              </button>
            </form>
          </div>
        </div>

        {/* Info sidebar */}
        <div className="space-y-4">
          <div className="neo-card p-6">
            <Mail className="text-[#00E676] mb-3" size={24} />
            <h3 className="font-bold text-sm uppercase tracking-wider mb-2">Email direct</h3>
            <a
              href="mailto:joseph@profimy-academie.com"
              className="text-[#00E676] font-bold text-sm hover:underline break-all"
              data-testid="direct-email-link"
            >
              joseph@profimy-academie.com
            </a>
          </div>

          <div className="neo-card p-6">
            <MessageSquare className="text-[#FFCC00] mb-3" size={24} />
            <h3 className="font-bold text-sm uppercase tracking-wider mb-2">Temps de réponse</h3>
            <p className="text-sm text-muted-foreground">
              Nous répondons généralement sous 24-48h
            </p>
          </div>

          <div className="neo-card p-6">
            <User className="text-[#00E676] mb-3" size={24} />
            <h3 className="font-bold text-sm uppercase tracking-wider mb-2">Support</h3>
            <p className="text-sm text-muted-foreground">
              Pour les questions relatives aux formations, consultez{" "}
              <a
                href="https://www.profimy-academie.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00E676] hover:underline"
              >
                Profimy Académie
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
